import React from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  MinusCircle,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import GuidePartnersPipeline from './GuidePartnersPipeline';
import ListenLearnRoom from './ListenLearnRoom';
import ReachDadsRoom from './ReachDadsRoom';
import StewardTheWorkRoom from './StewardTheWorkRoom';
import SupportDadsRoom from './SupportDadsRoom';
import SystemHealthRoom from './SystemHealthRoom';

const STATUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  attention: 'border-yellow-200 bg-yellow-100 text-yellow-900 dark:border-yellow-500/25 dark:bg-yellow-500/15 dark:text-yellow-100',
  next: 'border-aqua/30 bg-aqua/10 text-cyan-800 dark:border-aqua/25 dark:bg-aqua/10 dark:text-aqua',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
};

const SIGNAL_ICON_BY_TONE = {
  urgent: AlertCircle,
  attention: Clock3,
  next: ArrowRight,
  steady: CheckCircle2,
};

const METRIC_CARD_CLASSES = {
  urgent: 'border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/5',
  attention: 'border-yellow-200 bg-yellow-100/80 dark:border-yellow-500/20 dark:bg-yellow-500/10',
  next: 'border-aqua/30 bg-aqua/10 dark:border-aqua/20 dark:bg-aqua/5',
  steady: 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]',
};

const PRIORITY_CARD_CLASSES = {
  urgent: 'border-red-200 bg-red-50/80 dark:border-red-500/25 dark:bg-red-500/5',
  attention: 'border-yellow-200 bg-yellow-100/85 dark:border-yellow-500/25 dark:bg-yellow-500/10',
  next: 'border-aqua/30 bg-aqua/10 dark:border-aqua/20 dark:bg-aqua/5',
  steady: 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]',
};

function RoomSignal({ signal }) {
  const Icon = SIGNAL_ICON_BY_TONE[signal.tone] || MinusCircle;

  return (
    <div className="flex items-start gap-3 rounded-xl bg-white/70 px-3 py-2.5 dark:bg-white/[0.04]">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 dark:text-gray-400" />
      <p className="text-sm text-slate-700 dark:text-gray-300">{signal.text}</p>
    </div>
  );
}

function RoomMetric({ metric }) {
  return (
    <div className={`rounded-[1.25rem] border p-4 ${METRIC_CARD_CLASSES[metric.tone] || METRIC_CARD_CLASSES.steady}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
        {metric.label}
      </p>
      <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">{metric.value}</p>
      <p className="mt-1 text-sm text-slate-600 dark:text-gray-400">{metric.detail}</p>
    </div>
  );
}

function PriorityItem({ item, index }) {
  return (
    <div className={`rounded-[1.1rem] border p-4 ${PRIORITY_CARD_CLASSES[item.tone] || PRIORITY_CARD_CLASSES.steady}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-white">
          {index + 1}
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
          {item.eyebrow}
        </p>
      </div>
      <h4 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
        {item.title}
      </h4>
      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
        {item.detail}
      </p>
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500">
        {item.location}
      </p>
    </div>
  );
}

function WatchList({ title, items = [] }) {
  if (!items.length) return null;

  return (
    <Card className="border-slate-200 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.03]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
        {title}
      </p>
      <div className="mt-3 space-y-2">
        {items.map((item, index) => (
          <div key={`${item.title}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
            <p className="text-sm font-medium text-slate-900 dark:text-white">{item.title}</p>
            {item.detail ? (
              <p className="mt-1 text-xs tracking-[0.08em] text-slate-500 dark:text-gray-400">
                {item.detail}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function ActiveRoomContext({ context, onOpenTool }) {
  if (!context) return null;
  const scopeLabel = context.scope === 'zone' ? 'Inside this zone' : 'Inside this room';
  if (context.variant === 'guide-partners') {
    return <GuidePartnersPipeline context={{ ...context, onOpenTool }} />;
  }
  if (context.variant === 'reach-dads') {
    return <ReachDadsRoom context={{ ...context, onOpenTool }} />;
  }
  if (context.variant === 'listen-learn') {
    return <ListenLearnRoom context={context} />;
  }
  if (context.variant === 'support-dads') {
    return <SupportDadsRoom context={{ ...context, onOpenTool }} />;
  }
  if (context.variant === 'steward-the-work') {
    return <StewardTheWorkRoom context={{ ...context, onOpenTool }} />;
  }
  if (context.variant === 'system-health') {
    return <SystemHealthRoom context={{ ...context, onOpenTool }} />;
  }

  return (
    <div className="grid gap-3 xl:grid-cols-[1.2fr_0.8fr]">
      <Card className="border-slate-200 bg-gradient-to-br from-white via-slate-50 to-[#eef8f6] p-5 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#20232a] dark:via-[#191c22] dark:to-[#12161b]">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-aqua/80">
                {scopeLabel}
              </p>
              <h3 className="mt-3 text-2xl font-semibold leading-8 text-slate-900 dark:text-white">
                {context.startTitle}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
                {context.startDetail}
              </p>
            </div>
            <Badge className={STATUS_BADGE_CLASSES[context.statusTone] || STATUS_BADGE_CLASSES.steady}>
              {context.statusLabel}
            </Badge>
          </div>

          <div className="grid gap-2">
            {(context.signals || []).slice(0, 2).map((signal, index) => (
              <RoomSignal key={`context-signal-${index}`} signal={signal} />
            ))}
          </div>

          {context.primaryAction?.label || context.secondaryAction?.label ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <p className="mr-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
                Suggested flow
              </p>
              {context.primaryAction?.label ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-aqua/20 bg-aqua/10 px-3 py-1.5 text-sm text-cyan-900 dark:border-aqua/20 dark:bg-aqua/10 dark:text-aqua">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-aqua/80">
                    Start
                  </span>
                  <span className="font-medium">{context.primaryAction.label}</span>
                </div>
              ) : null}
              {context.secondaryAction?.label ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 dark:border-[#2a2a32] dark:bg-[#1a1d24] dark:text-gray-300">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                    Then
                  </span>
                  <span className="font-medium">{context.secondaryAction.label}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {(context.priorityItems || []).length ? (
            <div className="pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
                Your move
              </p>
              <div className="mt-3 grid gap-3 lg:grid-cols-3">
                {context.priorityItems.slice(0, 3).map((item, index) => (
                  <PriorityItem key={`${item.title}-${index}`} item={item} index={index} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
        {(context.metrics || []).slice(0, 3).map((metric, index) => (
          <RoomMetric key={`context-metric-${index}`} metric={metric} />
        ))}
        {(context.watchListItems || []).length ? (
          <div className="sm:col-span-3 xl:col-span-1">
            <WatchList
              title={context.watchListTitle || 'In view'}
              items={context.watchListItems}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
